import { ApplicationRef, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { ActionState, ModalService } from '@mkp/shared/ui-library';
import { versionEffectsActions } from '@mkp/core/state/actions';
import { SentryService } from '@mkp/core/util-sentry';
import { createPageVisibilityObservable } from '@mkp/shared/util-device';
import { createEffect } from '@ngrx/effects';
import {
  catchError,
  concatMap,
  filter,
  from,
  interval,
  map,
  of,
  switchMap,
  take,
  takeWhile,
  tap,
} from 'rxjs';

// Polling configuration
const ACTIVE_TAB_CHECK_INTERVAL = 1000 * 30; // Poll every 30s when tab is active
const INACTIVE_TAB_GRACE_PERIOD = 1000 * 60 * 2; // Stop polling after 2 minutes of inactivity

const pageVisibility$ = createPageVisibilityObservable();

/**
 * Check for service worker updates with polling based on tab visibility.
 *
 * - Polls every 30s when tab is visible
 * - Continues polling for up to 2 minutes after tab becomes hidden, then stops
 * - Resumes polling when tab becomes visible again
 * - Silently catches errors to prevent app crashes
 *
 * This prevents excessive ngsw.json requests (and resulting IP rate-limiting)
 * when users leave tabs open for extended periods.
 */
export const checkForUpdate = createEffect(
  (
    applicationRef = inject(ApplicationRef),
    swUpdate = inject(SwUpdate),
    sentryService = inject(SentryService)
  ) => {
    const performUpdateCheck = () =>
      from(swUpdate.checkForUpdate()).pipe(
        catchError((error: unknown) => {
          sentryService.captureWarning('[SW Update Check] Failed to check for updates', error);
          return of(false);
        })
      );

    return applicationRef.isStable.pipe(
      filter(Boolean),
      take(1),
      switchMap(() =>
        pageVisibility$.pipe(
          switchMap((isVisible) => {
            if (!isVisible) {
              return interval(ACTIVE_TAB_CHECK_INTERVAL).pipe(
                takeWhile(
                  (_, index) => (index + 1) * ACTIVE_TAB_CHECK_INTERVAL < INACTIVE_TAB_GRACE_PERIOD
                ),
                concatMap(() => performUpdateCheck())
              );
            }

            return interval(ACTIVE_TAB_CHECK_INTERVAL).pipe(concatMap(() => performUpdateCheck()));
          })
        )
      )
    );
  },
  {
    dispatch: false,
    functional: true,
  }
);

// when a new update is ready to be activated: update the state
export const listenForUpdateReady = createEffect(
  (swUpdate = inject(SwUpdate)) =>
    swUpdate.versionUpdates.pipe(
      filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'),
      map(() => versionEffectsActions.newVersionIsAvailable())
    ),
  {
    functional: true,
  }
);

// on unrecoverable state: we show a modal and force users to refresh the app
export const listenForUnrecoverableState = createEffect(
  (swUpdate = inject(SwUpdate), modalService = inject(ModalService), window = inject(Window)) =>
    swUpdate.unrecoverable.pipe(
      tap(() =>
        modalService.openDialog({
          primaryLabel: 'ERROR_UPDATE_DIALOG.RELOAD',
          state: ActionState.Warning,
          content: 'ERROR_UPDATE_DIALOG.TEXT',
          title: 'ERROR_UPDATE_DIALOG.TITLE',
          primaryDataTest: 'pwa-unrecoverable-modal-confirm-btn',
          secondaryDataTest: 'pwa-unrecoverable-modal-cancel-btn',
          action: () => swUpdate.activateUpdate().then(() => window.location.reload()),
        })
      ),
      take(1)
    ),
  { dispatch: false, functional: true }
);
